import React, { useEffect, useState, useMemo } from "react"
import {
    DataGrid,
    GridColumnMenuColumnsItem,
    GridColumnMenuContainer,
    GridColumnMenuFilterItem,
    GridColumnMenuSortItem,
    useGridApiRef,
} from "@mui/x-data-grid"
import PropTypes from "prop-types"
import { styled } from "@mui/material/styles"
import { Box, Button, Typography } from "@mui/material"
import { ReactComponent as TableBaseIcon } from "src/v2/assets/icons/table-base-icon.svg"
import SelectComponent from "../select/select-component"
import { localeBRDataGridText } from "src/utils/data-grid-local-text"
import { deepmerge } from '@mui/utils'
import CustomLoadingOverlay from "./custom-loading-overlay"
import { clampPage, filterRows, sortRows } from "./utils/client-data-operations"


const ButtonPagination = (props) => (
    <Button
        onClick={props?.handleClick}
        sx={{
            boxSizing: "border-box",
            border: "1px solid #FFFFFF",
            position: "relative",
            width: "48px",
            height: "48px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontStyle: "normal",
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "16px",
            textAlign: "center",
            color: "#FFFFFF",
            padding: 0,
            minWidth: "40px",
            backgroundColor: props?.isActive ? "#24346C" : "transparent",
            transition: "all 0.2s ease",
            "&:hover": {
                backgroundColor: props?.isActive ? "#24346C" : "#FFFFFF22",
                borderColor: "#FFFFFF",
            },
            "&:active": {
                backgroundColor: props?.isActive ? "#24346C" : "#FFFFFF44",
                borderColor: "#FFFFFF",
            },
        }}
    >
        {props?.value}
    </Button>
)
ButtonPagination.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    handleClick: PropTypes.func,
    isActive: PropTypes.bool,
}

const FooterPagination = ({
    totalPages,
    page,
    handlePageChange,
    handlePageSizeChange,
    valueSelectPaginationValue,
}) => {
    const pagesToShow = Math.min(totalPages, 3)
    const startPage = page
    const pageNumbers = Array.from(
        { length: pagesToShow },
        (_, index) => startPage + index
    ).filter((pageNumber) => pageNumber <= totalPages && pageNumber > 0)

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexFlow: "wrap",
                gap: 2,
                p: 1,
                mt: 3,
            }}
        >
            <Box sx={{ display: "flex", gap: 2 }}>
                <ButtonPagination
                    handleClick={() => handlePageChange(Math.max(1, page - 1))}
                    value={"<"}
                />

                {pageNumbers.map((pageNumber) => (
                    <ButtonPagination
                        key={pageNumber}
                        handleClick={() => handlePageChange(pageNumber)}
                        isActive={pageNumber === page}
                        value={pageNumber}
                    />
                ))}

                <ButtonPagination
                    handleClick={() =>
                        handlePageChange(Math.min(totalPages, page + 1))
                    }
                    value={">"}
                />
            </Box>
            <SelectComponent
                label="itens por página"
                value={valueSelectPaginationValue}
                onChange={(e) => {
                    handlePageSizeChange(`${e?.target?.value}`)
                }}
                options={["25", "50", "100"]}
                width="138px"
            />
        </Box>
    )
}

FooterPagination.propTypes = {
    totalPages: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    handlePageChange: PropTypes.func.isRequired,
    handlePageSizeChange: PropTypes.func.isRequired,
    pageSize: PropTypes.number.isRequired,
    valueSelectPaginationValue: PropTypes.number.isRequired,
}

const StyledGridColumnMenuContainer = styled(GridColumnMenuContainer)(() => ({
    backgroundColor: "#2E3448",
    "& .MuiMenuItem-root": {
        "& .MuiTypography-root": {
            fontSize: "14px",
            color: "#fff",
        },
        "&:hover": { backgroundColor: "#14192B" },
    },
    "& .MuiListItemIcon-root": {
        color: "#E3A107",
    },
}))

function CustomColumnMenu(props) {
    const itemProps = { colDef: props.colDef, onClick: props.hideMenu }
    return (
        <StyledGridColumnMenuContainer {...props}>
            <GridColumnMenuSortItem {...itemProps} />
            <GridColumnMenuFilterItem {...itemProps} />
            <GridColumnMenuColumnsItem {...itemProps} />
        </StyledGridColumnMenuContainer>
    )
}

CustomColumnMenu.propTypes = {
    colDef: PropTypes.object.isRequired,
    hideMenu: PropTypes.func.isRequired,
}

const NoRowOverlay = () => {
    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                color: "white",
                gap: "12px",
            }}
        >
            <TableBaseIcon />
            <Typography variant="textBody" sx={{ color: "#FFF !important" }}>
                Nenhum dado encontrado.
            </Typography>
        </Box>
    )
}

const DataGridTable = (
    {
        columns,
        rows,
        onRowClick,
        pagination = [25, 50, 100],
        showPagination = true,
        disableDeleteColumn,
        pageSize = 25,
        page = 1,
        resetKey,
        pointerCursor,
        autoRowHeight,
        rowHeight,
        serverSide = false,
        rowCount,
        rowPadding,
        onServerPageChange,
        contained = false,
        manualClientDataOperations = false,
        ...props
    }) => {
    const apiRef = useGridApiRef()
    const [filterModel, setFilterModel] = useState({ items: [] })
    const [sortModel, setSortModel] = useState([])

    const [pageSizeSelect, setPageSizeSelect] = useState(pageSize)
    const [pageSelected, setPageSelected] = useState(page)

    const filteredRows = useMemo(() => {
        if (manualClientDataOperations) {
            return filterRows(rows, columns, filterModel)
        }

        return rows.filter((row) => {
            return filterModel.items.every((f) => {
                if (!f.value) return true
                const cellValue = String(row[f.field] ?? "").toLowerCase()
                return cellValue.includes(String(f.value).toLowerCase())
            })
        })
    }, [columns, filterModel, manualClientDataOperations, rows])

    const sortedRows = useMemo(() => {
        if (!manualClientDataOperations) return filteredRows

        return sortRows(filteredRows, columns, sortModel)
    }, [columns, filteredRows, manualClientDataOperations, sortModel])

    const totalPages = showPagination
        ? serverSide
            ? Math.ceil((rowCount ?? 0) / pageSizeSelect)
            : Math.ceil(sortedRows.length / pageSizeSelect)
        : 1
    const selectedPage = clampPage(pageSelected, totalPages)
    const startIndex = showPagination ? (selectedPage - 1) * pageSizeSelect : 0
    const endIndex = showPagination ? startIndex + pageSizeSelect : sortedRows.length
    const visibleRows = serverSide ? rows : sortedRows.slice(startIndex, endIndex)

    useEffect(() => {
        setPageSelected(page)
    }, [filterModel, page, resetKey])

    useEffect(() => {
        setPageSelected((currentPage) => clampPage(currentPage, totalPages))
    }, [totalPages])

    const baseSx = {
        width: "100%",
        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            width: "16px",
            height: "16px",
            background: "transparent",
        },
        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-corner": {
            backgroundColor: "transparent",
        },
        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
            background: "transparent",
        },
        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
            backgroundColor: "#D9D9D9",
            opacity: "40%",
            borderRadius: "8px",
            border: `5px solid transparent`,
            backgroundClip: "padding-box",
            marginBlockStart: "16px",
        },
        "& .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-scrollbarFiller--header": {
            backgroundColor: "transparent",
        },

        "& .MuiDataGrid-menu .MuiMenuItem-root": {
            color: "darkblue",
        },
        "& .MuiListItemIcon-root": {
            color: "blue",
        },
        "& .MuiDataGrid-sortIcon": {
            color: "rgba(255,255,255,0.7)",
        },
        "& .MuiDataGrid-iconButtonContainer .MuiIconButton-root": {
            color: "rgba(255,255,255,0.7)",
        },
        "& .MuiDataGrid-menuIconButton": {
            color: "rgba(255,255,255,0.7)",
            fontSize: "20px",
            "&:hover": {
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.1)",
            },
        },
        "&>.MuiDataGrid-main": {
            "&>.MuiDataGrid-columnHeaders": {
                borderBottom: "none",
                backgroundColor: "transparent",
                color: "#FFFFFF",
            },

            "& div div div div >.MuiDataGrid-cell": {
                borderBottom: "none",
            },
        },
        "&.MuiDataGrid-root": {
            border: "none",
            color: "#FFFFFF",
            backgroundColor: "transparent",
            "& .MuiDataGrid-columnSeparator": {
                display: "none !important",
            },
            "& .MuiDataGrid-withBorderColor": {
                borderColor: "transparent",
            },
            "& .MuiDataGrid-columnHeaders": {
                borderBottom: "none !important",
            },
        },

        "& .MuiDataGrid-columnHeader": {
            color: "#FFFFFF",
            backgroundColor: "transparent",
            border: "none",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
            fontFamily: "'Titillium Web', sans-serif",
            fontStyle: "normal",
            fontWeight: 700,
            fontSize: "11px",
            lineHeight: "17px",
            textAlign: "center",
            textTransform: "uppercase",
            color: "#FFFFFF",
            opacity: 1,
        },
        "& .MuiDataGrid-cell": {
            py: rowPadding ?? 0,
            fontFamily: "'Roboto', sans-serif",
            fontStyle: "normal",
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "16px",
            display: "flex",
            alignItems: "center",
            color: "#FFFFFF !important",
            border: "none",
            backgroundColor: "transparent",
            outline: "none !important",
        },
        "& .MuiDataGrid-row": {
            border: "none",
            outline: "none",
        },
        "& .even": { backgroundColor: "rgba(255,255,255,0.04)" },
        "& .odd": { backgroundColor: "rgba(255, 255, 255, 0.09)" },
        "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover": {
            backgroundColor: "rgba(255,255,255,0.09)",
        },
        "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
        },
        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
        },
        ...(pointerCursor
            ? { "& .pointerCursor .MuiDataGrid-cell": { cursor: "pointer" } }
            : {}),
    }

    const mergedSx = deepmerge(baseSx, props.sx || {})

    return (
        <Box
            sx={{
                width: "100%",
                height: contained ? "100%" : "auto",
                minHeight: 0,
                display: contained ? "flex" : "block",
                flexDirection: contained ? "column" : undefined,
                overflow: contained ? "hidden" : "visible",
            }}
        >
            <DataGrid
                getRowHeight={() => autoRowHeight ? 'auto' : rowHeight ? rowHeight : undefined}
                apiRef={apiRef}
                columns={columns}
                rows={props?.loading ? [] : visibleRows}
                pagination={pagination}
                pageSize={pageSizeSelect}
                disableDeleteColumn={disableDeleteColumn}
                getRowClassName={(params) => {
                    const baseClass = params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                    if (pointerCursor) return baseClass + " pointerCursor"
                    return baseClass
                }}
                onFilterModelChange={setFilterModel}
                filterModel={filterModel}
                filterMode={manualClientDataOperations ? "server" : undefined}
                sortingMode={manualClientDataOperations ? "server" : undefined}
                sortModel={manualClientDataOperations ? sortModel : undefined}
                onSortModelChange={manualClientDataOperations ? setSortModel : undefined}
                disableVirtualization={!contained}
                hideFooter
                slots={{
                    columnMenu: CustomColumnMenu,
                    noRowsOverlay: NoRowOverlay,
                    loadingOverlay: (props) => (
                        <CustomLoadingOverlay columns={columns} {...props} />
                    ),
                }}
                page={selectedPage - 1}
                onPageChange={(newPage) => setPageSelected(newPage + 1)}
                autoHeight={!contained}
                localeText={localeBRDataGridText}
                slotProps={{
                    filterPanel: {
                        sx: {
                            "& .MuiInputBase-input": {
                                color: "#D9D9D9",
                                textAlign: "center",
                            },

                            "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                            },

                            "& .MuiMenuItem-root": {
                                justifyContent: "center",
                                textAlign: "center",
                            },

                            "& .MuiDataGrid-filterFormColumnInput, & .MuiDataGrid-filterFormOperatorInput, & .MuiDataGrid-filterFormValueInput":
                            {
                                "& .MuiInputLabel-root": {
                                    color: "white",
                                },

                                "& .MuiInputBase-input": {
                                    color: "rgba(255, 255, 255, 0.8)",
                                    textAlign: "center",
                                },

                                "& .MuiSelect-icon": {
                                    color: "white",
                                },

                                "& .MuiSelect-select": {
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    textAlign: "center",
                                },
                            },

                            "& .MuiSvgIcon-root": {
                                color: "#D9D9D9",
                            },
                        },
                    },

                    columnsPanel: {
                        sx: {
                            overflowX: "scroll",

                            "& .MuiFormControlLabel-label": {
                                color: "#fff",
                            },

                            "& .MuiSwitch-thumb": {
                                backgroundColor: "#E3A107",
                            },

                            "& .MuiSwitch-track": {
                                backgroundColor: "#555",
                            },

                            "& .MuiButton-root": {
                                color: "#E3A107",

                                "&:hover": {
                                    backgroundColor: "#14192B",
                                },
                            },

                            "& .MuiInputBase-input": {
                                color: "rgba(255,255,255,0.8)",
                            },
                        },
                    },
                }}
                sx={{
                    ...mergedSx,
                    flex: contained ? 1 : undefined,
                    minHeight: contained ? 0 : undefined,
                    "& .MuiDataGrid-virtualScroller": {
                        minHeight: contained ? 0 : "300px",
                        overflowX: "auto !important",
                        overflowY: "auto !important",
                    },
                }}
                onRowClick={onRowClick}
                {...props}
            />

            {showPagination && visibleRows?.length !== 0 && (
                <FooterPagination
                    totalPages={totalPages}
                    page={selectedPage}
                    handlePageChange={(value) => {
                        if (value === selectedPage) return
                        setPageSelected(value)
                        if (serverSide) onServerPageChange?.(value, pageSizeSelect)
                    }}
                    handlePageSizeChange={(value) => {
                        setPageSizeSelect(value)
                        setPageSelected(1)
                        if (serverSide) onServerPageChange?.(1, Number(value))
                    }}
                    valueSelectPaginationValue={pageSizeSelect}
                />
            )}
        </Box>
    )
}

DataGridTable.propTypes = {
    columns: PropTypes.array,
    rows: PropTypes.array,
    onRowClick: PropTypes.func,
    pagination: PropTypes.object,
    pageSize: PropTypes.number,
    page: PropTypes.number,
    disableDeleteColumn: PropTypes.bool,
    showPagination: PropTypes.bool,
    loading: PropTypes.bool,
    resetKey: PropTypes.string,
    sx: PropTypes.object,
    pointerCursor: PropTypes.bool,
    autoRowHeight: PropTypes.bool,
    rowHeight: PropTypes.number,
    serverSide: PropTypes.bool,
    rowCount: PropTypes.number,
    rowPadding: PropTypes.number,
    onServerPageChange: PropTypes.func,
    contained: PropTypes.bool,
    manualClientDataOperations: PropTypes.bool,
}

export default DataGridTable
